import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// Import of the module

import { ExternalLink } from "@/components/ExternalLink";
import i18n from "@/i18n";
import { parseDate } from "@/lib/utils";

import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";

// Type of the fields in the table

export type TimeSerieFileDate = {
  id: number;
  status: string;
  valuation_date: string | null;
  generation_date: string | null;
  strategy_name?: string;
  strategy_id?: number | null;
  time_serie_file_name?: string;
  time_serie_file_id?: number | null;
  error_message?: string | null;
  created_at: string;
  extracted_validated?: boolean;
  enriched_validated?: boolean;
  inquiring?: boolean;
  is_python_etl?: boolean;
};

// Definition of the columns

export const time_serie_file_dates_columns: ColumnDef<TimeSerieFileDate>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "status",
    header: "Status",
  },
  {
    accessorKey: "valuation_date",
    header: "Valuation Date",
    cell: ({ row }) => {
      return row.getValue("valuation_date")
        ? parseDate(row.getValue("valuation_date"))
        : "";
    },
  },
  {
    accessorKey: "generation_date",
    header: "Generation Date",
    cell: ({ row }) => {
      return row.getValue("generation_date")
        ? parseDate(row.getValue("generation_date"))
        : "";
    },
  },
  {
    accessorKey: "strategy_name",
    header: "Strategy Name",
    cell: ({ row }) => {
      const strategyId = row.original.strategy_id;
      const name = row.getValue("strategy_name") as string | undefined;
      if (strategyId && name) {
        return (
          <ExternalLink
            href={toWorkspacePath(
              `/admin/qtl - referential/strategies/${strategyId}`,
            )}
            className="text-xs max-w-[200px] truncate block" // limit width + ellipsis
            underline="hover"
            title={name} // show full name on hover
          >
            {name}
          </ExternalLink>
        );
      }
      return "";
    },
  },
  {
    accessorKey: "strategy_id",
    header: "Strategy ID",
    cell: ({ row }) => {
      const id = row.getValue("strategy_id");
      return id !== undefined && id !== null ? id : "";
    },
  },
  {
    accessorKey: "time_serie_file_name",
    header: "Time Series File Name",
    cell: ({ row }) => {
      const tsfId = row.original.time_serie_file_id;
      const name = row.getValue("time_serie_file_name") as string | undefined;

      if (tsfId && name) {
        const maxLength = 40; // adjust as needed
        const displayName =
          name.length > maxLength ? name.slice(0, maxLength) + "…" : name;

        return (
          <ExternalLink
            href={toWorkspacePath(`/admin/qtl - etl/time_serie_files/${tsfId}`)}
            className="text-xs lg:whitespace-nowrap"
            underline="hover"
          >
            {displayName}
          </ExternalLink>
        );
      }
      return "";
    },
  },
  {
    accessorKey: "time_serie_file_id",
    header: "Time Series File ID",
    cell: ({ row }) => {
      const id = row.getValue("time_serie_file_id");
      return id !== undefined && id !== null ? id : "";
    },
  },
  {
    accessorKey: "error_message",
    header: "Error Message",
    cell: ({ row }) => row.getValue("error_message") || "",
  },
  {
    accessorKey: "created_at",
    header: "Created At",
    cell: ({ row }) => {
      return row.getValue("created_at")
        ? parseDate(row.getValue("created_at"))
        : "";
    },
  },
  {
    accessorKey: "extracted_validated",
    header: "Extracted Validated",
    cell: ({ row }) => {
      const value = row.getValue("extracted_validated");
      return value === true ? "Yes" : value === false ? "No" : "";
    },
  },
  {
    accessorKey: "enriched_validated",
    header: "Enriched Validated",
    cell: ({ row }) => {
      const value = row.getValue("enriched_validated");
      return value === true ? "Yes" : value === false ? "No" : "";
    },
  },
  {
    accessorKey: "inquiring",
    header: "Inquiring",
    cell: ({ row }) => {
      const value = row.getValue("inquiring");
      return value === true ? "Yes" : value === false ? "No" : "";
    },
  },
  {
    accessorKey: "is_python_etl",
    header: "Python ETL",
    cell: ({ row }) => {
      const value = row.getValue("is_python_etl");
      return value === true ? "Yes" : value === false ? "No" : "";
    },
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
